/**
 * Utilidad para impresión térmica vía Bluetooth
 * Compatible con impresoras térmicas que soporten ESC/POS
 */

/**
 * Comandos ESC/POS básicos
 */
const ESC = '\x1B';
const GS = '\x1D';

const Commands = {
  INIT: ESC + '@',
  ALIGN_CENTER: ESC + 'a' + '\x01',
  ALIGN_LEFT: ESC + 'a' + '\x00',
  ALIGN_RIGHT: ESC + 'a' + '\x02',
  BOLD_ON: ESC + 'E' + '\x01',
  BOLD_OFF: ESC + 'E' + '\x00',
  SIZE_NORMAL: GS + '!' + '\x00',
  SIZE_DOUBLE: GS + '!' + '\x11',
  SIZE_LARGE: GS + '!' + '\x22',
  LINE_FEED: '\n',
  CUT_PAPER: GS + 'V' + '\x41' + '\x00',
  UNDERLINE_ON: ESC + '-' + '\x01',
  UNDERLINE_OFF: ESC + '-' + '\x00',
};

/**
 * Clase para manejar la conexión Bluetooth con la impresora
 */
class BluetoothThermalPrinter {
  constructor() {
    this.device = null;
    this.characteristic = null;
    this.encoder = new TextEncoder();
  }

  /**
   * Elimina tildes y caracteres especiales para compatibilidad con impresoras térmicas
   */
  normalizeText(text) {
    if (!text) return '';
    
    return text
      // Reemplazar tildes
      .replace(/[áàäâ]/g, 'a')
      .replace(/[éèëê]/g, 'e')
      .replace(/[íìïî]/g, 'i')
      .replace(/[óòöô]/g, 'o')
      .replace(/[úùüû]/g, 'u')
      .replace(/[ÁÀÄÂ]/g, 'A')
      .replace(/[ÉÈËÊ]/g, 'E')
      .replace(/[ÍÌÏÎ]/g, 'I')
      .replace(/[ÓÒÖÔ]/g, 'O')
      .replace(/[ÚÙÜÛ]/g, 'U')
      // Reemplazar ñ
      .replace(/ñ/g, 'n')
      .replace(/Ñ/g, 'N')
      // Reemplazar otros caracteres especiales
      .replace(/ç/g, 'c')
      .replace(/Ç/g, 'C')
      .replace(/ý/g, 'y')
      .replace(/ÿ/g, 'y')
      .replace(/Ý/g, 'Y')
      // Eliminar caracteres no imprimibles
      .replace(/[^\x20-\x7E]/g, '');
  }

  /**
   * Envía datos a la impresora con normalización de texto
   */
  async write(data) {
    if (!this.characteristic) {
      throw new Error('No hay conexión con la impresora');
    }

    // Normalizar el texto si es string
    const normalizedData = typeof data === 'string' ? this.normalizeText(data) : data;
    const encoded = typeof normalizedData === 'string' ? this.encoder.encode(normalizedData) : normalizedData;
    
    // Dividir en chunks de 512 bytes (límite de Bluetooth LE)
    const chunkSize = 512;
    for (let i = 0; i < encoded.length; i += chunkSize) {
      const chunk = encoded.slice(i, i + chunkSize);
      await this.characteristic.writeValue(chunk);
      // Pequeña pausa entre chunks
      await new Promise(resolve => setTimeout(resolve, 50));
    }
  }

  /**
   * Verifica si el navegador soporta Web Bluetooth
   */
  isSupported() {
    return 'bluetooth' in navigator;
  }

  /**
   * Conecta con una impresora Bluetooth
   */
  async connect() {
    if (!this.isSupported()) {
      throw new Error('Web Bluetooth no está soportado en este navegador');
    }

    try {
      // Solicitar dispositivo Bluetooth - Acepta cualquier dispositivo
      // El usuario seleccionará manualmente su impresora
      this.device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: [
          '000018f0-0000-1000-8000-00805f9b34fb', // Servicio común de impresoras
          '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Servicio HM-10 (común en impresoras chinas)
          'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // Servicio de impresoras Bluetooth LE
        ]
      });

      console.log('Dispositivo seleccionado:', this.device.name);

      // Conectar al servidor GATT
      const server = await this.device.gatt.connect();
      console.log('Conectado al servidor GATT');

      // Intentar obtener servicios disponibles
      let service = null;
      let characteristic = null;

      // Lista de UUIDs comunes de servicios de impresoras
      const serviceUUIDs = [
        '000018f0-0000-1000-8000-00805f9b34fb',
        '49535343-fe7d-4ae5-8fa9-9fafd205e455',
        'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
      ];

      // Lista de UUIDs comunes de características de escritura
      const characteristicUUIDs = [
        '00002af1-0000-1000-8000-00805f9b34fb',
        '49535343-8841-43f4-a8d4-ecbe34729bb3',
        'bef8d6c9-9c21-4c9e-b632-bd58c1009f9f',
      ];

      // Intentar conectar con diferentes combinaciones de servicios/características
      for (const serviceUUID of serviceUUIDs) {
        try {
          service = await server.getPrimaryService(serviceUUID);
          console.log('Servicio encontrado:', serviceUUID);
          
          for (const charUUID of characteristicUUIDs) {
            try {
              characteristic = await service.getCharacteristic(charUUID);
              console.log('Característica encontrada:', charUUID);
              this.characteristic = characteristic;
              return true;
            } catch (e) {
              // Intentar siguiente característica
              continue;
            }
          }
        } catch (e) {
          // Intentar siguiente servicio
          continue;
        }
      }

      // Si no se encontró ninguna combinación, lanzar error
      throw new Error('No se pudo encontrar un servicio compatible en la impresora');
      
    } catch (error) {
      console.error('Error conectando a la impresora:', error);
      throw error;
    }
  }

  /**
   * Desconecta de la impresora
   */
  disconnect() {
    if (this.device && this.device.gatt.connected) {
      this.device.gatt.disconnect();
      console.log('Desconectado de la impresora');
    }
    this.device = null;
    this.characteristic = null;
  }

  /**
   * Verifica si está conectado
   */
  isConnected() {
    return this.device && this.device.gatt && this.device.gatt.connected && this.characteristic;
  }

  /**
   * Obtiene información del dispositivo conectado
   */
  getDeviceInfo() {
    if (this.device) {
      return {
        name: this.device.name || 'Desconocido',
        id: this.device.id || 'N/A',
        connected: this.device.gatt?.connected || false
      };
    }
    return null;
  }

  /**
   * Envía datos a la impresora
   */
  async write(data) {
    if (!this.characteristic) {
      throw new Error('No hay conexión con la impresora');
    }

    const encoded = typeof data === 'string' ? this.encoder.encode(data) : data;
    
    // Dividir en chunks de 512 bytes (límite de Bluetooth LE)
    const chunkSize = 512;
    for (let i = 0; i < encoded.length; i += chunkSize) {
      const chunk = encoded.slice(i, i + chunkSize);
      await this.characteristic.writeValue(chunk);
      // Pequeña pausa entre chunks
      await new Promise(resolve => setTimeout(resolve, 50));
    }
  }

  /**
   * Imprime texto con normalización de caracteres
   */
  async printText(text) {
    const normalizedText = this.normalizeText(text);
    await this.write(normalizedText + Commands.LINE_FEED);
  }

  /**
   * Imprime línea divisoria
   */
  async printDivider(char = '-', length = 32) {
    await this.printText(char.repeat(length));
  }

  /**
   * Formatea una línea colocando un texto a la derecha
   */
  formatLineWithRightText(left, right, width = 32) {
    const cleanLeft = this.normalizeText(left || '');
    const cleanRight = this.normalizeText(right || '');

    if (!cleanRight) {
      return cleanLeft;
    }

    const maxLeftLength = Math.max(0, width - cleanRight.length - 1);
    const trimmedLeft = cleanLeft.length > maxLeftLength
      ? cleanLeft.slice(0, maxLeftLength)
      : cleanLeft;

    const spaces = Math.max(1, width - trimmedLeft.length - cleanRight.length);
    return `${trimmedLeft}${' '.repeat(spaces)}${cleanRight}`;
  }

  /**
   * Imprime un recibo de recolección
   */
  async printReceipt(recoleccion) {
    try {
      // Inicializar impresora
      await this.write(Commands.INIT);

      const totalPrendas = recoleccion.prendas?.reduce((sum, prenda) => sum + (prenda.cantidad || 0), 0) || recoleccion.cantidad_prendas || 0;

      // Encabezado
      await this.write(Commands.ALIGN_CENTER);
      await this.write(Commands.SIZE_LARGE);
      await this.write(Commands.BOLD_ON);
      await this.printText('SANIMAX');
      await this.write(Commands.SIZE_NORMAL);
      await this.write(Commands.BOLD_OFF);
      await this.printText('Lavanderia Proflux');
      await this.printText(recoleccion.planta?.nombre || 'Planta');
      await this.printDivider('=');

      // Consecutivo
      await this.write(Commands.SIZE_DOUBLE);
      await this.write(Commands.BOLD_ON);
      await this.printText(recoleccion.consecutivo || 'N/A');
      await this.write(Commands.SIZE_NORMAL);
      await this.write(Commands.BOLD_OFF);
      await this.printDivider();

      // Información
      await this.write(Commands.ALIGN_LEFT);
      const fecha = new Date().toLocaleString('es-CO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      await this.printText(`Fecha: ${fecha}`);
      await this.printText(`Empleado: ${recoleccion.empleado?.nombre || 'N/A'}`);
      await this.printText(`Cedula: ${recoleccion.empleado?.cedula || 'N/A'}`);
      await this.printText(`Area: ${recoleccion.empleado?.area?.nombre || 'N/A'}`);
      await this.printText(`Estado: ${this.getEstadoLabel(recoleccion.estado)}`);
      await this.printDivider();

      // Prendas
      await this.write(Commands.ALIGN_CENTER);
      await this.write(Commands.BOLD_ON);
      await this.printText('PRENDAS RECIBIDAS');
      await this.write(Commands.BOLD_OFF);
      await this.write(Commands.ALIGN_LEFT);
      await this.printDivider();

      if (recoleccion.prendas && recoleccion.prendas.length > 0) {
        for (let i = 0; i < recoleccion.prendas.length; i++) {
          const prenda = recoleccion.prendas[i];
          await this.write(Commands.BOLD_ON);
          const quantityText = `x${prenda.cantidad || 1}`;
          const nameLine = this.formatLineWithRightText(`${i + 1}. ${prenda.nombre}`, quantityText);
          await this.printText(nameLine);
          await this.write(Commands.BOLD_OFF);
          
          if (prenda.descripcion) {
            await this.printText(`   Desc: ${prenda.descripcion}`);
          }
          if (prenda.observaciones) {
            await this.printText(`   Obs: ${prenda.observaciones}`);
          }
          await this.printText('');
        }
      } else {
        await this.printText('No hay prendas registradas');
      }

      await this.printDivider('=');

      // Total
      await this.write(Commands.ALIGN_CENTER);
      await this.write(Commands.BOLD_ON);
      const totalLine = this.formatLineWithRightText('TOTAL PRENDAS', `${totalPrendas}`);
      await this.printText(totalLine);
      await this.write(Commands.BOLD_OFF);
      await this.printDivider();

      // Firma
      await this.printText('');
      await this.printText('');
      await this.printText('_______________________');
      await this.printText('Firma del Empleado');
      await this.printText('');

      // Footer
      await this.printText(`Recibido por: ${recoleccion.creator?.nombre || 'Sistema'}`);
      await this.printText('');
      await this.printText('Gracias por su confianza');
      await this.printText('www.sanimax.com');
      await this.printText('');
      await this.printText('');
      await this.printText('');

      // Cortar papel (opcional, depende de la impresora)
      // await this.write(Commands.CUT_PAPER);

      console.log('Recibo impreso exitosamente');
      return true;
    } catch (error) {
      console.error('Error imprimiendo recibo:', error);
      throw error;
    }
  }

  /**
   * Imprime un recibo de entrega
   */
  async printEntregaReceipt(entrega) {
    try {
      // Inicializar impresora
      await this.write(Commands.INIT);

      const fecha = entrega.fechaEntrega 
        ? new Date(entrega.fechaEntrega).toLocaleString('es-CO', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })
        : new Date().toLocaleString('es-CO', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });

      const totalPrendas = entrega.prendas?.reduce((sum, prenda) => sum + (prenda.cantidad || 0), 0) || entrega.cantidadTotal || 0;

      // Encabezado
      await this.write(Commands.ALIGN_CENTER);
      await this.write(Commands.SIZE_LARGE);
      await this.write(Commands.BOLD_ON);
      await this.printText('SANIMAX');
      await this.write(Commands.SIZE_NORMAL);
      await this.write(Commands.BOLD_OFF);
      await this.printText('Lavanderia Proflux');
      await this.printText(entrega.planta?.nombre || 'Planta');
      await this.printDivider('=');

      // Título y consecutivo
      await this.write(Commands.BOLD_ON);
      await this.printText('COMPROBANTE DE ENTREGA');
      await this.write(Commands.BOLD_OFF);
      await this.printText(entrega.consecutivo || 'ENT-000000');
      await this.printDivider();

      // Información del empleado
      await this.write(Commands.ALIGN_LEFT);
      await this.write(Commands.BOLD_ON);
      await this.printText('DATOS DEL EMPLEADO');
      await this.write(Commands.BOLD_OFF);
      await this.printDivider();
      await this.printText(`Fecha:   ${fecha}`);
      await this.printText(`Nombre:  ${entrega.empleado?.nombre || 'N/A'}`);
      await this.printText(`Cedula:  ${entrega.empleado?.cedula || 'N/A'}`);
      await this.printText(`Area:    ${entrega.empleado?.area?.nombre || 'N/A'}`);
      await this.printDivider();

      // Prendas entregadas
      await this.write(Commands.ALIGN_CENTER);
      await this.write(Commands.BOLD_ON);
      await this.printText('PRENDAS ENTREGADAS');
      await this.write(Commands.BOLD_OFF);
      await this.write(Commands.ALIGN_LEFT);
      await this.printDivider();

      if (entrega.prendas && entrega.prendas.length > 0) {
        for (let i = 0; i < entrega.prendas.length; i++) {
          const prenda = entrega.prendas[i];
          await this.write(Commands.BOLD_ON);
          const quantityText = `x${prenda.cantidad || 1}`;
          const nameLine = this.formatLineWithRightText(`${i + 1}. ${prenda.nombre}`, quantityText);
          await this.printText(nameLine);
          await this.write(Commands.BOLD_OFF);

          if (prenda.descripcion) {
            await this.printText(`   Desc: ${prenda.descripcion}`);
          }
          if (prenda.observaciones) {
            await this.printText(`   Obs: ${prenda.observaciones}`);
          }
          if (prenda.recoleccionConsecutivo) {
            await this.printText(`   Recoleccion: ${prenda.recoleccionConsecutivo}`);
          }
          await this.printText('');
        }
      } else {
        await this.printText('No hay prendas registradas');
      }

      await this.printDivider('=');

      // Total prendas
      await this.write(Commands.ALIGN_CENTER);
      await this.write(Commands.BOLD_ON);
      await this.printText(`TOTAL PRENDAS: ${totalPrendas}`);
      await this.write(Commands.SIZE_NORMAL);
      await this.write(Commands.BOLD_OFF);
      await this.printDivider();

      // Firma
      await this.printText('');
      await this.printText('_______________________');
      await this.printText('Firma del Empleado');
      await this.printText('');
      await this.printText('Confirmo haber recibido las');
      await this.printText('prendas descritas en este');
      await this.printText('comprobante');
      await this.printText('');

      // Footer
      await this.write(Commands.ALIGN_CENTER);
      await this.printText(`Entregado por: ${entrega.entregadoPor?.nombre || 'Sistema'}`);
      await this.printText('Gracias por su confianza');
      await this.printText('www.sanimax.com');
      await this.printText('');

      // Cortar papel (opcional, depende de la impresora)
      // await this.write(Commands.CUT_PAPER);

      console.log('Recibo de entrega impreso exitosamente');
      return true;
    } catch (error) {
      console.error('Error imprimiendo recibo de entrega:', error);
      throw error;
    }
  }

  /**
   * Obtiene la etiqueta del estado
   */
  getEstadoLabel(estado) {
    const estados = {
      'recibido': 'RECIBIDO',
      'pendiente_verificacion': 'PENDIENTE VERIFICACION',
      'verificado': 'VERIFICADO',
      'rechazado': 'RECHAZADO',
      'entregado': 'ENTREGADO'
    };
    return estados[estado] || estado?.toUpperCase() || 'N/A';
  }
}

// Instancia singleton
let printerInstance = null;

/**
 * Obtiene la instancia de la impresora
 */
export const getPrinterInstance = () => {
  if (!printerInstance) {
    printerInstance = new BluetoothThermalPrinter();
  }
  return printerInstance;
};

/**
 * Conecta e imprime un recibo
 */
export const printBluetoothReceipt = async (recoleccion) => {
  const printer = getPrinterInstance();
  
  if (!printer.isSupported()) {
    throw new Error('Tu navegador no soporta Web Bluetooth. Usa Chrome, Edge o Opera en Android.');
  }

  try {
    // Si no está conectado, conectar primero
    if (!printer.characteristic) {
      await printer.connect();
    }

    // Imprimir recibo
    await printer.printReceipt(recoleccion);
    
    return true;
  } catch (error) {
    console.error('Error en impresión Bluetooth:', error);
    throw error;
  }
};

/**
 * Conecta e imprime un recibo de entrega
 */
export const printBluetoothEntregaReceipt = async (entrega) => {
  const printer = getPrinterInstance();
  
  if (!printer.isSupported()) {
    throw new Error('Tu navegador no soporta Web Bluetooth. Usa Chrome, Edge o Opera en Android.');
  }

  try {
    // Si no está conectado, conectar primero
    if (!printer.characteristic) {
      await printer.connect();
    }

    // Imprimir recibo de entrega
    await printer.printEntregaReceipt(entrega);
    
    return true;
  } catch (error) {
    console.error('Error en impresión Bluetooth de entrega:', error);
    throw error;
  }
};

/**
 * Imprime etiqueta de alistamiento vía Bluetooth
 */
export const printBluetoothAlistamiento = async (alistamiento) => {
  const printer = getPrinterInstance();

  if (!printer.isSupported()) {
    throw new Error('Tu navegador no soporta Web Bluetooth. Usa Chrome, Edge u Opera en Android.');
  }

  if (!printer.characteristic) {
    await printer.connect();
  }

  const n = (t) => printer.normalizeText(t || '');
  const fecha = new Date().toLocaleString('es-CO', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });

  await printer.write(Commands.INIT);
  await printer.write(Commands.ALIGN_CENTER);
  await printer.write(Commands.BOLD_ON);
  await printer.write(Commands.SIZE_DOUBLE);
  await printer.write('SANIMAX\n');
  await printer.write(Commands.SIZE_NORMAL);
  await printer.write('ETIQUETA DE ALISTAMIENTO\n');
  await printer.write(Commands.BOLD_OFF);
  await printer.write('================================\n');

  await printer.write(Commands.ALIGN_LEFT);
  await printer.write(Commands.BOLD_ON);
  await printer.write(`Consec: ${n(alistamiento.consecutivo)}\n`);
  await printer.write(Commands.BOLD_OFF);
  await printer.write(`Fecha : ${fecha}\n`);
  await printer.write(`Planta: ${n(alistamiento.planta?.nombre)}\n`);
  await printer.write('--------------------------------\n');

  await printer.write(Commands.BOLD_ON);
  await printer.write(`${n(alistamiento.empleado?.nombre)} ${n(alistamiento.empleado?.apellido)}\n`);
  await printer.write(`Cedula: ${n(alistamiento.empleado?.cedula)}\n`);
  await printer.write(`Area  : ${n(alistamiento.empleado?.area?.nombre)}\n`);
   await printer.write(Commands.BOLD_OFF);
  await printer.write('--------------------------------\n');

  await printer.write(Commands.BOLD_ON);
  await printer.write('PRENDAS\n');
  await printer.write(Commands.BOLD_OFF);

  const prendas = alistamiento.prendas || [];
  const COL = 32; // ancho útil de la línea
  for (let i = 0; i < prendas.length; i++) {
    const p = prendas[i];
    const nombre = n(p.nombre);
    const cant = String(p.cantidad);
    // nombre alineado a izquierda, cantidad alineada a derecha
    const pad = COL - nombre.length - cant.length;
    const linea = nombre + (pad > 0 ? ' '.repeat(pad) : ' ') + cant;
    await printer.write(`${linea}\n`);
  }

  const totalUds = prendas.reduce((s, p) => s + (p.cantidad || 0), 0);
  await printer.write('================================\n');
  await printer.write(Commands.BOLD_ON);
  const totalLabel = 'TOTAL';
  const totalCant = String(totalUds) + ' uds';
  const totalPad = COL - totalLabel.length - totalCant.length;
  await printer.write(`${totalLabel}${' '.repeat(Math.max(1, totalPad))}${totalCant}\n`);
  await printer.write(Commands.BOLD_OFF);
  await printer.write('\n\n\n');
  await printer.write(Commands.CUT_PAPER);

  return true;
};

/**
 * Desconecta la impresora
 */
export const disconnectPrinter = () => {
  const printer = getPrinterInstance();
  printer.disconnect();
  printerInstance = null;
};

export default BluetoothThermalPrinter;
